import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { TextField, Checkbox, Autocomplete, Grid } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { TagPicker } from 'rsuite';
import { Box, Typography } from '@mui/material';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;


export const buildQueryString = (filterObj, mapping, createIsoStringWithTimezone, dateType, extraParams = {}) => {
  const params = new URLSearchParams();

  Object.entries(mapping).forEach(([key, param]) => {
    if (filterObj[key]?.length > 0) {
      params.append(param, filterObj[key].join(','));
    }
  });

  const startDate = filterObj.start_datetime;
  const endDate = filterObj.end_datetime;

  const isoStartDate = createIsoStringWithTimezone(startDate);
  const isoEndDate = createIsoStringWithTimezone(endDate);

  if (isoStartDate) params.append('start_date', isoStartDate);
  if (isoEndDate) params.append('end_date', isoEndDate);

  Object.entries(extraParams).forEach(([key, value]) => {
    if (value !== undefined) params.append(key, value);
  });

  if (dateType) {
    params.append('use_datetime_type', dateType);
  }

  return params;
};


export const renderAutocomplete = ({
  label,
  value,
  options = [],
  filterKey,
  multiple = true,
  placeholder,
  disabled = false,
  helperText = '',
  handleFilterChange,
  isLoading = false,
}) => {
  const safeOptions = Array.isArray(options) ? options : [];
  const safeValue = multiple
    ? (Array.isArray(value) ? value : [])
    : (value ?? null);

  const safeHandleChange = typeof handleFilterChange === 'function'
    ? handleFilterChange
    : () => {};

  return (
    <Autocomplete
      multiple={multiple}
      options={safeOptions}
      disableCloseOnSelect={multiple}
      value={safeValue}
      onChange={(_, newValue) => safeHandleChange(filterKey, newValue)}
      loading={!!isLoading}
      disabled={!!disabled}
      noOptionsText="No options"
      loadingText="Loading..."
      getOptionLabel={(option) => {
        if (option == null) return '';
        if (typeof option === 'string') return option;
        return option.label || option.value || String(option);
      }}
      isOptionEqualToValue={(option, value) => {
        if (option == null || value == null) return option === value;
        if (typeof option === 'string' && typeof value === 'string') {
          return option === value;
        }
        return option === value || option.value === value || option === value.value;
      }}
      renderOption={(props, option, { selected }) => {
        if (option == null) return null;
        
        const displayValue = typeof option === 'string' ? option : (option.label || option.value || String(option));
        
        return (
          <li {...props} key={displayValue}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {displayValue}
          </li>
        );
      }}
      renderInput={(params) => {
        if (!params) {
          return (
            <TextField
              label={label || ''}
              placeholder={placeholder || (label ? `Select ${label}` : '')}
              variant="outlined"
              size="small"
              helperText={helperText || ''}
              disabled={disabled}
              sx={{
                '& .MuiOutlinedInput-root': {
                  paddingTop: '4px',
                  paddingBottom: '4px'
                }
              }}
            />
          );
        }

        return (
          <TextField
            {...params}
            label={label || ''}
            placeholder={placeholder || (label ? `Select ${label}` : '')}
            variant="outlined"
            size="small"
            helperText={helperText || ''}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingTop: '4px',
                paddingBottom: '4px'
              }
            }}
          />
        );
      }}
    />
  );
};

export const RSuiteMultiSelect = ({
  label,
  value,
  options = [],
  filterKey,
  multiple = true,
  placeholder,
  disabled = false,
  helperText = '',
  handleFilterChange,
  isLoading = false,
  icon: Icon,
}) => {
  const [localValue, setLocalValue] = useState(value || []);
  const [placement, setPlacement] = useState('bottomStart');
  const containerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const initialSafeValue = multiple
    ? (Array.isArray(value) ? value : [])
    : (value ?? null);
  const prevValueRef = useRef(initialSafeValue);

  const safeOptions = Array.isArray(options) ? options : [];
  const safeValue = useMemo(() => {
    return multiple
      ? (Array.isArray(value) ? value : [])
      : (value ?? null);
  }, [multiple, value]);

  const safeHandleChange = typeof handleFilterChange === 'function'
    ? handleFilterChange
    : () => {};

  const arraysEqual = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return a === b;
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
  };

  const valueKey = useMemo(() => {
    if (Array.isArray(value)) {
      return JSON.stringify(value);
    }
    return value;
  }, [value]);

  useEffect(() => {
    const prevValue = prevValueRef.current;
    const currentValue = safeValue || [];
    
    if (Array.isArray(prevValue) && Array.isArray(currentValue)) {
      if (!arraysEqual(prevValue, currentValue)) {
        setLocalValue(currentValue);
        prevValueRef.current = currentValue;
      }
    } else if (prevValue !== currentValue) {
      setLocalValue(currentValue);
      prevValueRef.current = currentValue;
    }
  }, [valueKey]);

  const debouncedHandleChange = (newValue) => {
    setLocalValue(newValue);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      safeHandleChange(filterKey, newValue || []);
    }, 800);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const calculatePlacement = useCallback(() => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedMenuHeight = 300;
    
    if (spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow) {
      setPlacement('topStart');
    } else {
      setPlacement('bottomStart');
    }
  }, []);

  useEffect(() => {
    calculatePlacement();
    window.addEventListener('resize', calculatePlacement);
    window.addEventListener('scroll', calculatePlacement, true);

    return () => {
      window.removeEventListener('resize', calculatePlacement);
      window.removeEventListener('scroll', calculatePlacement, true);
    };
  }, [calculatePlacement]);

  const rsuiteOptions = safeOptions
    .filter((option) => option != null)
    .map((option) => {
      if (typeof option === 'string') {
        return { label: option, value: option };
      }
      return {
        label: option?.label || option?.value || String(option || ''),
        value: option?.value || option?.label || String(option || ''),
      };
    });

  const rsuiteValue = multiple
    ? (Array.isArray(localValue) ? localValue : [])
    : (localValue ? [localValue] : []);

  return (
    <Box ref={containerRef} sx={{ mb: 0.5 }}>
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {Icon && <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
        </Box>
      )}
      <TagPicker
        data={rsuiteOptions}
        value={rsuiteValue}
        onChange={(value) => debouncedHandleChange(value || [])}
        onOpen={calculatePlacement}
        placeholder={placeholder || (label ? `Select ${label}` : 'Select...')}
        disabled={disabled}
        searchable
        creatable={false}
        placement={placement}
        style={{ width: '100%' }}
        renderValue={(value, items, selectedElement) => {
          if (!value || value.length === 0) {
            return <span style={{ color: '#999' }}>{placeholder || 'Select...'}</span>;
          }
          if (value.length > 3) {
            return (
              <span>
                {selectedElement.slice(0, 2)}
                <span style={{ marginLeft: 4, color: '#666' }}>
                  +{value.length - 2} more
                </span>
              </span>
            );
          }
          return selectedElement;
        }}
        menuStyle={{ maxHeight: '300px', overflow: 'auto' }}
      />
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export const renderRSuiteMultiSelect = (props) => {
  if (props.onChange && !props.handleFilterChange) {
    const { onChange, ...restProps } = props;
    return (
      <RSuiteMultiSelect
        {...restProps}
        handleFilterChange={(filterKey, value) => {
          onChange(value);
        }}
        filterKey="onChange"
      />
    );
  }
  return <RSuiteMultiSelect {...props} />;
};

export const generateDropdownComponents = (autocompleteConfigs, renderAutocompleteFn) => (
  <Grid container direction="column" spacing={0.5}>
    {autocompleteConfigs.map((config) => (
      <Grid key={config.filterKey}>
        {renderAutocompleteFn(config)}
      </Grid>
    ))}
  </Grid>
);

export const sortingToOrdering = (sorting) => {
  if (!sorting || sorting.length === 0) return null;
  return sorting.map(s => (s.desc ? `-${s.id}` : s.id)).join(',');
};

export const orderingToSorting = (orderingStr) => {
  if (!orderingStr || typeof orderingStr !== 'string') return null;
  return orderingStr.split(',').filter(Boolean).map(part => {
    const desc = part.startsWith('-');
    return { id: desc ? part.slice(1) : part, desc };
  });
};

export const exportTableToCSV = (data, columns, filename = 'table-export.csv') => {
  if (!data || data.length === 0) {
    return;
  }

  const visibleColumns = columns.filter(
    col => (col.accessorKey || col.accessorFn) && 
    !col.id?.startsWith('mrt-') &&
    col.id !== 'mrt-row-actions' &&
    col.id !== 'mrt-row-expand' &&
    col.id !== 'mrt-row-select'
  );

  const headers = visibleColumns.map(col => col.header || col.accessorKey || col.id);

  const rows = data.map(row => {
    return visibleColumns.map(col => {
      let value = '';
      
      if (col.accessorFn) {
        value = col.accessorFn(row) ?? '';
      } else if (col.accessorKey) {
        const accessorKey = col.accessorKey;
        if (accessorKey.includes('.')) {
          const keys = accessorKey.split('.');
          value = keys.reduce((obj, key) => obj?.[key], row) ?? '';
        } else {
          value = row[accessorKey] ?? '';
        }
      }

      if (value === null || value === undefined) {
        value = '';
      } else if (Array.isArray(value)) {
        value = value.map(item => {
          if (typeof item === 'object' && item !== null) {
            const parts = Object.entries(item)
              .map(([k, v]) => `${k}: ${v}`)
              .join(', ');
            return `{${parts}}`;
          }
          return String(item);
        }).join('; ');
      } else if (typeof value === 'object' && value !== null) {
        const parts = Object.entries(value)
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
        value = `{${parts}}`;
      } else if (typeof value === 'boolean') {
        value = value ? 'True' : 'False';
      }

      const stringValue = String(value ?? '');
      
      if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      
      return stringValue;
    });
  });

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};


const COLUMN_DEFAULTS = {
  id:                              { size: 80,  noWrap: true },
  vin:                             { size: 180, noWrap: true },
  mac_id:                          { size: 150, noWrap: true },
  mac_addr:                        { size: 180, noWrap: true },
  requested_mac_addr:              { size: 180, noWrap: true },
  ip:                              { size: 150, noWrap: true },
  vescom:                          { size: 120, noWrap: true },
  year:                            { size: 80,  noWrap: true },
  did:                             { size: 100, noWrap: true },
  elapsed:                         { size: 100, noWrap: true },
  file_size:                       { size: 100, noWrap: true },
  module:                          { size: 120, noWrap: true },
  diagnostic_id:                   { size: 130, noWrap: true },
  ecu_code:                        { size: 110, noWrap: true },
  status_mask:                     { size: 120, noWrap: true },
  failure_type_byte:               { size: 120, noWrap: true },
  preexisting:                     { size: 120, noWrap: true },
  status_new:                      { size: 120, noWrap: true },

  program:                         { size: 140 },
  region:                          { size: 150 },
  email:                           { size: 250 },
  display_name:                    { size: 200 },
  user:                            { size: 250 },
  requested_by_detail:             { size: 220 },
  reviewed_by_detail:              { size: 220 },
  renewed_by_detail:               { size: 220 },
  requested_to_detail:             { size: 220 },
  device_detail:                   { size: 160 },
  replacement_device_detail:       { size: 160 },
  trigger_type:                    { size: 200 },
  suite_name:                      { size: 200 },
  trip:                            { size: 200 },

  is_active:                       { size: 110, noWrap: true },
  is_lost:                         { size: 100, noWrap: true },
  is_broken:                       { size: 100, noWrap: true },
  status:                          { size: 120, noWrap: true },
  dtc_type:                        { size: 120, noWrap: true },
  device_registration_status:      { size: 160 },
  tailscale_approval_status:       { size: 150 },
  request_type:                    { size: 170 },
  request_type_display:            { size: 170 },
  requested_device_type:           { size: 160 },
  requested_pi:                    { size: 130 },
  pi:                              { size: 180 },

  date:                            { size: 180, noWrap: true },
  datetime:                        { size: 180, noWrap: true },
  datetime_upload:                 { size: 180, noWrap: true },
  datetime_uploaded:               { size: 180, noWrap: true },
  start_time:                      { size: 180, noWrap: true },
  end_time:                        { size: 180, noWrap: true },
  last_activity:                   { size: 180, noWrap: true },
  requested_at:                    { size: 180, noWrap: true },
  reviewed_at:                     { size: 180, noWrap: true },
  completed_at:                    { size: 180, noWrap: true },
  renewed_at:                      { size: 180, noWrap: true },
  expiry_date:                     { size: 180, noWrap: true },
  device_registration_expiry_date: { size: 180, noWrap: true },
  tailscale_approval_expiry_date:  { size: 180, noWrap: true },

  filename:                        { size: 350 },
  response:                        { size: 250 },
  summary:                         { size: 250 },
  reason:                          { size: 250 },
  rejection_reason:                { size: 250 },
  notes:                           { size: 250 },
  dtc_description:                 { size: 250 },
  ecu_description:                 { size: 200 },
  failure_reasons:                 { size: 350 },
  suite_source:                    { size: 350 },
  additional_data:                 { size: 350 },
  allowed_regions:                 { size: 250 },
};

export const applyColumnDefaults = (columns) => {
  const estimateHeaderMinSize = (header) => {
    if (typeof header !== 'string') return null;
    const trimmedHeader = header.trim();
    if (!trimmedHeader) return null;

    // Reserve room for sort/filter/menu icons in MRT header cells.
    const iconReservePx = 132;
    const approxCharWidthPx = 9;
    const estimated = (trimmedHeader.length * approxCharWidthPx) + iconReservePx;
    return Math.min(460, Math.max(150, estimated));
  };

  return columns.map((col) => {
    const key = col.accessorKey || col.id;
    const defaults = key ? COLUMN_DEFAULTS[key] : null;
    const applied = { ...col };
    const defaultSize = defaults?.size;
    const headerMinSize = estimateHeaderMinSize(applied.header);

    if (applied.size === undefined) {
      if (defaultSize && headerMinSize) {
        applied.size = Math.max(defaultSize, headerMinSize);
      } else if (defaultSize) {
        applied.size = defaultSize;
      } else if (headerMinSize) {
        applied.size = headerMinSize;
      }
    } else if (headerMinSize) {
      applied.size = Math.max(applied.size, headerMinSize);
    }

    if (headerMinSize && applied.size !== undefined && applied.minSize === undefined) {
      applied.minSize = Math.max(applied.size, headerMinSize);
    }

    if (defaults?.noWrap && !applied.muiTableBodyCellProps) {
      applied.muiTableBodyCellProps = { sx: { whiteSpace: 'nowrap' } };
    }

    return applied;
  });
};